import * as React from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  ShieldCheck,
  User as UserIcon,
  type LucideIcon,
} from "lucide-react";
import { useMe, type PrimaryPersona } from "@/api/me";
import { useMfaStatus } from "@/api/mfa";
import { cn } from "@/lib/cn";
import { useViewport } from "@/lib/useViewport";
import { useAuthStore } from "@/store/authStore";

/**
 * Personal settings shell (MV2-865, canvas 04 owner/Profile … SettingsMobile).
 *
 * Desktop: "Settings / Your account" head and a four-tab subnav — Profile ·
 * Notifications · Security · Access — the same four for every persona.
 * Mobile (~70% of owner traffic): `/settings` is a list of the four sections
 * (all reachable — Notifications was missing on mobile before), each child
 * page gets a "‹ Settings" head instead of a tab strip.
 */

interface Section {
  to: string;
  label: string;
  icon: LucideIcon;
}

export const SETTINGS_SECTIONS: readonly Section[] = [
  { to: "/settings/my-profile", label: "Profile", icon: UserIcon },
  { to: "/settings/notifications", label: "Notifications", icon: Bell },
  { to: "/settings/security", label: "Security", icon: ShieldCheck },
  { to: "/settings/invitations", label: "Access", icon: KeyRound },
];

const PERSONA_LABEL: Record<PrimaryPersona, string> = {
  SuperAdmin: "Super Admin",
  ClientAdmin: "Client Admin",
  OM: "O&M engineer",
  SiteOwner: "Site Owner",
};

export function personaLabel(p: PrimaryPersona | null | undefined): string {
  return p ? PERSONA_LABEL[p] : "Member";
}

export function SettingsShell(): React.ReactElement {
  const { isCompact } = useViewport();
  const { pathname } = useLocation();
  const atIndex = pathname.replace(/\/+$/, "") === "/settings";
  const current = SETTINGS_SECTIONS.find((s) => pathname.startsWith(s.to));

  if (isCompact) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="flex flex-col gap-3 px-4 pb-24 pt-3.5">
          {atIndex ? (
            <div className="type-eyebrow">Settings</div>
          ) : (
            <div className="flex items-center gap-1">
              <Link
                to="/settings"
                className="inline-flex items-center gap-0.5 rounded-[6px] py-1 pr-2 text-[13px] text-gray-500 hover:text-gray-900"
              >
                <ChevronLeft size={15} aria-hidden /> Settings
              </Link>
              <span className="text-[15px] font-semibold text-gray-900">
                {current?.label ?? "Your account"}
              </span>
            </div>
          )}
          <Outlet />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-[1100px] px-6 pb-8">
        <header className="flex flex-col gap-0.5 pb-3.5 pt-5">
          <div className="type-eyebrow">Settings</div>
          <h1 className="text-[22px] font-semibold leading-tight text-gray-900">Your account</h1>
        </header>
        <nav
          aria-label="Settings sections"
          className="-mx-6 mb-[22px] flex gap-0.5 border-b border-gray-200 bg-surface px-6"
        >
          {SETTINGS_SECTIONS.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              className={({ isActive }) =>
                cn(
                  "relative whitespace-nowrap px-3 py-2.5 text-[13px] transition-colors duration-hover",
                  isActive
                    ? "font-medium text-gray-900 after:absolute after:inset-x-3 after:-bottom-px after:h-0.5 after:bg-gray-900"
                    : "text-gray-500 hover:text-gray-900",
                )
              }
            >
              {s.label}
            </NavLink>
          ))}
        </nav>
        <div className="max-w-[920px]">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/**
 * `/settings` itself: on desktop, straight to Profile; on mobile, the four
 * cards (owner/SettingsMobile) with a one-line "Signed in as" footer.
 */
export function SettingsIndex(): React.ReactElement {
  const { isCompact } = useViewport();
  const { data: me } = useMe();
  const mfa = useMfaStatus();
  const signOutAll = useAuthStore((s) => s.signOutAll);
  if (!isCompact) return <Navigate to="/settings/my-profile" replace />;

  const subtitle: Record<string, string> = {
    Profile: [me?.full_name, me?.email].filter(Boolean).join(" · ") || "Name, contact, appearance",
    Notifications: "How we reach you — in the app and by email",
    Security:
      mfa.data === undefined
        ? "Two-step sign-in, password, devices"
        : mfa.data.method !== "none"
          ? "Two-step on · password · devices"
          : "Two-step off · password · devices",
    Access: "The plants you can see and offers waiting",
  };

  return (
    <div className="flex flex-col gap-3">
      {SETTINGS_SECTIONS.map((s) => (
        <Link
          key={s.to}
          to={s.to}
          className="flex items-center gap-3 rounded-[10px] border border-gray-200 bg-surface p-3.5 outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <s.icon size={18} aria-hidden className="flex-none text-gray-500" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[13.5px] font-medium text-gray-900">{s.label}</span>
            <span className="type-meta truncate">{subtitle[s.label]}</span>
          </span>
          <ChevronRight size={16} aria-hidden className="flex-none text-gray-400" />
        </Link>
      ))}
      <p className="type-meta px-1 py-2">
        Signed in as {personaLabel(me?.primary_persona)}
        {me?.client?.name ? ` of ${me.client.name}` : ""} ·{" "}
        <button
          type="button"
          className="underline underline-offset-2 hover:text-gray-900"
          onClick={() => void signOutAll()}
        >
          Sign out
        </button>
      </p>
    </div>
  );
}
